import React, { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { ExclamationCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import type { Control, FieldValues, RegisterOptions, FieldErrors } from 'react-hook-form'
import Modal from '../Modal'

interface YearRangePickerProps {
  name: string
  onChange: (value: [Date | null, Date | null]) => void
  Controller: React.ComponentType<any>
  control: Control<FieldValues>
  rules?: RegisterOptions
  errors?: FieldErrors
  className?: string
  containerClass?: string
  value?: [Date | null, Date | null]
  disabled?: boolean
}

const YearRangePicker = (props: YearRangePickerProps) => {
  const {
    name,
    onChange: onChangeProp,
    Controller,
    control,
    rules,
    errors,
    className,
    containerClass = 'sm:max-w-xs',
    value = [null, null],
    disabled = false,
    ...otherProps
  } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [fromYear, toYear] = value || [null, null]
  const [currentDecade, setCurrentDecade] = useState(2020) // Start decade
  const [manualFromYear, setManualFromYear] = useState('')
  const [manualToYear, setManualToYear] = useState('')
  const isManuallyTyping = useRef(false)
  const fromYearInputRef = useRef<HTMLInputElement>(null)

  // Generate year options for current decade (10 years per page)
  const yearsPerPage = 20
  const startYear = currentDecade
  const endYear = currentDecade + yearsPerPage - 1
  const years = Array.from(
    { length: yearsPerPage },
    (_, i) => startYear + i
  )

  // Sync manual input fields when value changes from clicking (but not when manually typing)
  useEffect(() => {
    if (!isManuallyTyping.current) {
      if (fromYear) {
        setManualFromYear(fromYear.getFullYear().toString())
      } else {
        setManualFromYear('')
      }

      if (toYear) {
        setManualToYear(toYear.getFullYear().toString())
      } else {
        setManualToYear('')
      }
    }
  }, [fromYear, toYear])

  const handleYearSelect = (year: number) => {
    if (!fromYear || (fromYear && toYear)) {
      // Start new selection
      const newFromYear = new Date(year, 0, 1)
      onChangeProp([newFromYear, null])
    } else if (fromYear && !toYear) {
      // Complete selection
      const newToYear = new Date(year, 11, 31)
      if (year >= fromYear.getFullYear()) {
        onChangeProp([fromYear, newToYear])
      } else {
        // If selected year is before fromYear, swap them
        const newFromYear = new Date(year, 0, 1)
        onChangeProp([newFromYear, fromYear])
      }
      // Don't auto-close - let user manually close with Done button
    }
  }

  const handleManualEntry = (fromYearValue: string, toYearValue: string) => {
    const fromYearNum = parseInt(fromYearValue, 10)
    const toYearNum = parseInt(toYearValue, 10)

    if (fromYearNum && toYearNum && fromYearNum <= toYearNum) {
      const newFromYear = new Date(fromYearNum, 0, 1)
      const newToYear = new Date(toYearNum, 11, 31)
      onChangeProp([newFromYear, newToYear])

      // Navigate to the decade that contains the selected years
      const targetDecade = Math.floor(fromYearNum / yearsPerPage) * yearsPerPage
      setCurrentDecade(targetDecade)
    }
  }

  const handleClear = () => {
    onChangeProp([null, null])
    setManualFromYear('')
    setManualToYear('')
    setIsModalOpen(false)
  }

  const handlePreviousDecade = () => {
    setCurrentDecade(prev => prev - yearsPerPage)
  }

  const handleNextDecade = () => {
    setCurrentDecade(prev => prev + yearsPerPage)
  }

  const formatDisplayValue = () => {
    if (fromYear && toYear) {
      return `${fromYear.getFullYear()} - ${toYear.getFullYear()}`
    } else if (fromYear) {
      return `${fromYear.getFullYear()} - end year`
    }
    return 'Select years'
  }

  const classes = classNames(
    'block w-full shadow-xs pr-10 pl-7 sm:text-sm border rounded-md',
    {
      'border-cerise-red-300 text-cerise-red-900 placeholder-red-300 focus:outline-hidden focus:ring-cerise-red focus:border-cerise-red': errors?.[name],
      'focus:ring-perfect-shine focus:border-perfect-shine block w-full border-gray-300': !errors?.[name],
      'cursor-pointer': !disabled,
      'cursor-not-allowed opacity-50': disabled,
    },
    className,
  )

  return (
    <div className="flex flex-col">
      <div className={`relative ${containerClass}`}>
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={() => (
            <div
              className={`${classes} min-h-10 flex items-center bg-white`}
              onClick={() => !disabled && setIsModalOpen(true)}
            >
              <span className={`${fromYear && toYear ? 'text-gray-900' : 'text-gray-500'} whitespace-nowrap`}>
                {formatDisplayValue()}
              </span>
            </div>
          )}
        />

        {errors?.[name] && (
          <div className="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-cerise-red-500" aria-hidden="true"/>
          </div>
        )}

        <Modal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          wrapClasses="sm:max-w-lg"
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Select Year Range
              </h3>

              <div className="text-sm text-gray-600 mb-4">
                {!fromYear ?
                  'Click a year to start your range or enter manually below' :
                  !toYear ?
                    `Selected: ${fromYear.getFullYear()} - Click end year` :
                    `Selected: ${fromYear.getFullYear()} - ${toYear.getFullYear()}`
                }
              </div>

              {/* Manual Entry Section */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Manual Entry</h4>
                <div className="flex items-center gap-2 justify-center">
                  <input
                    ref={fromYearInputRef}
                    type="number"
                    placeholder="From year"
                    value={manualFromYear}
                    onChange={(e) => {
                      isManuallyTyping.current = true
                      const newValue = e.target.value
                      setManualFromYear(newValue)
                      if (newValue && manualToYear) {
                        handleManualEntry(newValue, manualToYear)
                      }
                      setTimeout(() => { isManuallyTyping.current = false }, 100)
                    }}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-sm"
                    min="1900"
                    max="2100"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="number"
                    placeholder="To year"
                    value={manualToYear}
                    onChange={(e) => {
                      isManuallyTyping.current = true
                      const newValue = e.target.value
                      setManualToYear(newValue)
                      if (manualFromYear && newValue) {
                        handleManualEntry(manualFromYear, newValue)
                      }
                      setTimeout(() => { isManuallyTyping.current = false }, 100)
                    }}
                    className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-sm"
                    min="1900"
                    max="2100"
                  />
                </div>
              </div>

              {/* Year Grid with Pagination */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={handlePreviousDecade}
                    className="flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    {startYear - yearsPerPage} - {startYear - 1}
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    {startYear} - {endYear}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextDecade}
                    className="flex items-center px-2 py-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    {endYear + 1} - {endYear + yearsPerPage}
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>

                <div className="grid grid-cols-5 gap-2">
                  {years.map((year) => {
                    const isFromYear = fromYear && year === fromYear.getFullYear()
                    const isToYear = toYear && year === toYear.getFullYear()
                    const isInRange = fromYear && toYear &&
                      year >= fromYear.getFullYear() &&
                      year <= toYear.getFullYear()

                    return (
                      <button
                        key={year}
                        type="button"
                        onClick={() => handleYearSelect(year)}
                        className={classNames(
                          'px-3 py-2 text-sm rounded-sm border',
                          {
                            'bg-bonfire-dark text-white border-bonfire-dark': isFromYear || isToYear,
                            'bg-perfect-shine text-white border-perfect-shine': isInRange && !isFromYear && !isToYear,
                            'bg-white text-gray-700 border-gray-300 hover:bg-gray-50': !isInRange && !isFromYear && !isToYear,
                          }
                        )}
                      >
                        {year}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="flex justify-center gap-2">
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-perfect-shine border border-transparent rounded-md hover:bg-perfect-shine-dark"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>

      {errors && (
        <p className="mt-2 text-sm text-cerise-red-600 select-none min-h-5">
          {(errors?.[name]?.message as string) || ''}
        </p>
      )}
    </div>
  )
}

export default YearRangePicker
